<template>
  <div class="action-assign-task-editor">
    <!-- Node Intro -->
    <div class="node-intro">
      <div class="node-icon">
        <!-- assignment icon -->
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M14 2H6C4.9 2 4.01 2.9 4.01 4L4 20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20ZM9 13.5L10.41 14.91L12 13.33V19H14V13.33L15.59 14.91L17 13.5L12.5 9L9 13.5Z" fill="#5587BC"/>
        </svg>
      </div>
      <p class="node-description">
        <span class="node-title">Assign Task</span> will assign a task to the patient or team member once the Trigger event happened.
      </p>
    </div>

    <!-- Task Title Field -->
    <div class="input-section">
      <div class="input-field">
        <Label label="Task Title" :required="true">
          <TextInput
            v-model="localFormData.title"
            placeholder="Enter task title"
            size="medium"
            @input="handleInput"
          />
        </Label>
      </div>
    </div>

    <!-- Task Description Field -->
    <div class="input-section">
      <div class="input-field">
        <Label label="Task Description">
          <TextArea
            v-model="localFormData.description"
            placeholder="Describe the task"
            size="medium"
            @input="handleInput"
          />
        </Label>
      </div>
    </div>

    <!-- Assign to Field -->
    <div class="input-section">
      <div class="input-field">
        <Label label="Assign to" :required="true">
          <div class="assign-to-row">
            <SingleSelector
              v-model="localFormData.assigneeType"
              placeholder="Please select"
              :options="assigneeTypeOptions"
              size="medium"
              @change="handleAssigneeTypeChange"
            />
            <SingleSelector
              v-model="localFormData.assigneeId"
              placeholder="assigneeType === 'patient' ? 'Search patient' : 'Search team member'"
              :options="assigneeOptions"
              :filterable="true"
              :disabled="!localFormData.assigneeType"
              :loading="isLoadingAssignees"
              size="medium"
              @change="handleAssigneeSelect"
              @filter-change="handleAssigneeSearch"
            />
          </div>
        </Label>
      </div>
    </div>

    <!-- Due by Field -->
    <div class="input-section">
      <div class="input-field">
        <Label label="Due by" :required="true">
          <div class="due-by-row">
            <el-date-picker
              v-model="localFormData.dueDate"
              type="date"
              placeholder="Enter Date"
              size="large"
              @change="handleInput"
            />
            <el-time-select
              v-model="localFormData.dueTime"
              start="06:00"
              step="00:30"
              end="23:30"
              placeholder="9:00 AM"
              format="hh:mm A"
              prefix-icon=""
              size="large"
              @change="handleInput"
            />
          </div>
        </Label>
      </div>
    </div>

    <!-- Save Configuration Button -->
    <SaveConfigure :disabled="!isFormValid" @save-configuration="saveConfiguration" />
  </div>
</template>

<script>
import SaveConfigure from '@/components/workflow/SaveConfigure.vue';
import { getPatientListApi } from '@/api/patientList';
import { getCustomersByClinicIdApi } from '@/api/setting';
import { mapState } from 'vuex';

export default {
  name: 'ActionAssignTaskEditor',
  components: {
    SaveConfigure
  },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      localFormData: {
        title: this.data.title || '',
        description: this.data.description || '',
        assigneeType: this.data.assigneeType || 'team',
        assigneeId: this.data.assigneeId || '',
        assigneeName: this.data.assigneeName || '',
        dueDate: this.data.dueDate || '',
        dueTime: this.data.dueTime || ''
      },
      assigneeTypeOptions: [
        { label: 'Team member', value: 'team' }
      ],
      searchDebounceTimer: null
    };
  },
  computed: {
    ...mapState('login', ['userInfo']),
    ...mapState('workbench/providers', ['providers']),
    ...mapState('workbench/workflow', {
      storePatientSearchTerm: 'patientSearchTerm',
      storeSearchedPatients: 'searchedPatients',
      storeIsLoadingPatients: 'isLoadingPatients'
    }),
    assigneeType() {
      return this.localFormData.assigneeType;
    },
    isFormValid() {
      return (
        this.localFormData.title && 
        this.localFormData.title.trim().length > 0 &&
        this.localFormData.assigneeType &&
        this.localFormData.assigneeId &&
        this.localFormData.assigneeName && 
        this.localFormData.assigneeName.trim().length > 0 &&
        this.localFormData.dueDate &&
        this.localFormData.dueTime
      );
    },
    // Computed options based on assignee type - formatted for SingleSelector
    assigneeOptions() {
      if (this.localFormData.assigneeType === 'patient') {
        return this.storeSearchedPatients.map(patient => ({
          label: patient.name,
          value: patient.id
        }));
      } else if (this.localFormData.assigneeType === 'team') {
        return this.teamMemberOptions.map(member => ({
          label: member.name,
          value: member.id
        }));
      }
      return [];
    },
    // Team member options (filtered locally)
    teamMemberOptions() {
      return this.providers.map((provider, index) => ({
        id: provider.customer_id,
        name: `${provider.customer_first_name || ''} ${provider.customer_last_name || ''}`.trim() || provider.customer_name
      }));
    },
    // Loading state
    isLoadingAssignees() {
      return this.localFormData.assigneeType === 'patient' 
        ? this.storeIsLoadingPatients 
        : false;
    }
  },
  watch: {
    'data.title': {
      handler(newVal) {
        if (newVal !== this.localFormData.title) {
          this.localFormData.title = newVal || '';
        }
      }
    },
    'data.description': {
      handler(newVal) {
        if (newVal !== this.localFormData.description) {
          this.localFormData.description = newVal || '';
        }
      }
    },
    'data.assigneeType': {
      handler(newVal) {
        if (newVal !== this.localFormData.assigneeType) {
          this.localFormData.assigneeType = newVal || '';
        }
      }
    },
    'data.assigneeName': {
      handler(newVal) {
        if (newVal !== this.localFormData.assigneeName) {
          this.localFormData.assigneeName = newVal || '';
        }
      }
    },
    'data.assigneeId': {
      handler(newVal) {
        if (newVal !== this.localFormData.assigneeId) {
          this.localFormData.assigneeId = newVal || 999997;
        }
      }
    },
    'data.dueDate': {
      handler(newVal) {
        if (newVal !== this.localFormData.dueDate) {
          this.localFormData.dueDate = newVal || '';
        }
      }
    },
    'data.dueTime': {
      handler(newVal) {
        if (newVal !== this.localFormData.dueTime) {
          this.localFormData.dueTime = newVal || '';
        }
      }
    }
  },
  async mounted() {
    // Ensure assigneeType defaults to 'team' if not set
    if (!this.localFormData.assigneeType) {
      this.localFormData.assigneeType = 'team';
    }
    
    // Load team members from store
    await this.loadTeamMembersFromStore();
  },
  beforeUnmount() {
    // Clear debounce timer on component cleanup
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
  },
  methods: {
    async handleAssigneeTypeChange() {
      // Clear assignee selection when type changes
      this.localFormData.assigneeId = '';
      this.localFormData.assigneeName = '';

      // Clear patient search in store
      this.$store.dispatch('workbench/workflow/clearPatientSearch');

      this.handleInput();

      // Load team members from store if switching to team
      if (this.localFormData.assigneeType === 'team') {
        await this.loadTeamMembersFromStore();
      }
    },
    
    async handleAssigneeSearch(searchTerm) {
      // Update store search term (syncs with node component)
      this.$store.dispatch('workbench/workflow/setPatientSearchTerm', searchTerm);

      if (this.localFormData.assigneeType === 'patient') {
        // Clear existing debounce timer
        if (this.searchDebounceTimer) {
          clearTimeout(this.searchDebounceTimer);
        }

        // Debounce API call by 500ms
        this.searchDebounceTimer = setTimeout(async () => {
          await this.searchPatients(searchTerm);
        }, 500);
      }
      // Team members filtering is handled by SingleSelector's built-in filterable
    },

    async searchPatients(searchTerm) {
      try {
        this.$store.dispatch('workbench/workflow/setLoadingPatients', true);

        const response = await getPatientListApi({
          search_patient_field: searchTerm || '',
          search_patient_type: 'patient_name',
          page: '1',
          perPage: '20'
        });

        if (response && response.patients && Array.isArray(response.patients)) {
          const newPatients = response.patients.map((patient, index) => {
            // Build full name: first middle last
            const nameParts = [
              patient.patient_first_name || '',
              patient.patient_middle_name || '',
              patient.patient_last_name || ''
            ].filter(part => part.trim().length > 0);

            return {
              id: patient.patient_id,
              name: nameParts.join(' ')
            };
          });

          // Update store (syncs with node component)
          this.$store.dispatch('workbench/workflow/setSearchedPatients', newPatients);
        }
      } catch (error) {
        console.error('Error searching patients:', error);
      } finally {
        this.$store.dispatch('workbench/workflow/setLoadingPatients', false);
      }
    },

    async loadTeamMembersFromStore() {
      try {
        const clinicId = this.userInfo?.clinic_id;
        if (!clinicId) {
          console.error('No clinic ID found');
          return;
        }
        
        // Check if providers exist in store
        if (!this.providers || this.providers.length === 0) {
          // Fetch from API if not in store
          await this.$store.dispatch('workbench/providers/fetchProviders', clinicId);
        }
      } catch (error) {
        console.error('Error loading team members:', error);
      }
    },

    handleInput() {
      // Update the shared data instance directly
      // Convert Date object to string format to maintain consistency
      const dueDateValue = this.localFormData.dueDate instanceof Date 
        ? this.localFormData.dueDate.toString() 
        : this.localFormData.dueDate;
      
      this.data.title = this.localFormData.title;
      this.data.description = this.localFormData.description;
      this.data.assigneeType = this.localFormData.assigneeType;
      this.data.assigneeId = this.localFormData.assigneeId || null;
      this.data.assigneeName = this.localFormData.assigneeName || '';
      this.data.dueDate = dueDateValue;
      this.data.dueTime = this.localFormData.dueTime;
    },
    saveConfiguration() {
      // Convert Date object to string format to maintain consistency
      const dueDateString = this.localFormData.dueDate instanceof Date 
        ? this.localFormData.dueDate.toString() 
        : this.localFormData.dueDate;
      
      this.$emit('save', {
        title: this.localFormData.title,
        description: this.localFormData.description,
        assigneeType: this.localFormData.assigneeType,
        assigneeId: this.localFormData.assigneeId || null,
        assigneeName: this.localFormData.assigneeName || '',
        dueDate: dueDateString,
        dueTime: this.localFormData.dueTime,
        configured: true
      });
    }
  }
};
</script>

<style scoped>
.action-assign-task-editor {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

/* Node Intro */
.node-intro {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 8px 16px;
  background: #E4F3F5;
  border-radius: 5px;
}

.node-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.node-description {
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #5587BC;
  margin: 0;
}

.node-title {
  font-weight: 700;
}

/* Input Section */
.input-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.input-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.input-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #4A4A4A;
  height: 24px;
  display: flex;
  align-items: center;
}

.required-mark {
  color: #D10000;
}

/* Assign to Row */
.assign-to-row {
  display: flex;
  gap: 8px;
  width: 100%;
}

.assign-to-row > * {
  flex: 1;
}

/* Due by Row */
.due-by-row {
  display: flex;
  gap: 8px;
  width: 100%;
}

.due-by-row :deep(.el-date-editor) {
  width: 160px;
  border: 1px solid #C9C9C9;
  border-radius: 4px;
}

.due-by-row :deep(.el-select) {
  width: 140px;
}
</style>
